/**
 * VincularLocaisModal
 *
 * Dual-list transfer modal for linking counting locations to a client-hospital pair.
 * Pattern cloned from VincularHospitaisModal.tsx
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, MapPin, Search, ArrowRight, ArrowLeft, Loader2, Save } from 'lucide-react';
import clienteHospitalLocalService from '../../services/cliente/clienteHospitalLocalService';
import type { LocalVinculado, LocalDisponivel } from '../../services/cliente/clienteHospitalLocalService';
import { msg } from '../../utils/messages';
import { useConfirm } from '../../hooks/useConfirm';

interface VincularLocaisModalProps {
  clienteId: number;
  hospitalId: number;
  hospitalNome: string;
  onClose: () => void;
  onSave: () => void;
}

const VincularLocaisModal: React.FC<VincularLocaisModalProps> = ({
  clienteId,
  hospitalId,
  hospitalNome,
  onClose,
  onSave,
}) => {
  const { confirm, ConfirmDialog } = useConfirm();

  const [disponiveis, setDisponiveis] = useState<LocalDisponivel[]>([]);
  const [vinculados, setVinculados] = useState<LocalVinculado[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  const [selectedAvailable, setSelectedAvailable] = useState<number[]>([]);
  const [selectedLinked, setSelectedLinked] = useState<number[]>([]);

  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');

  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoadingData(true);
      const [disp, vinc] = await Promise.all([
        clienteHospitalLocalService.getLocaisDisponiveis(clienteId, hospitalId),
        clienteHospitalLocalService.getLocaisVinculados(clienteId, hospitalId),
      ]);
      setDisponiveis(disp);
      setVinculados(vinc);
    } catch (error) {
      console.error('Erro ao carregar locais:', error);
      msg.error('Erro ao carregar locais de contagem');
    } finally {
      setLoadingData(false);
    }
  }, [clienteId, hospitalId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredAvailable = useMemo(() => {
    const search = searchLeft.trim().toLowerCase();
    if (!search) return disponiveis;
    return disponiveis.filter(l =>
      l.local.toLowerCase().includes(search) ||
      l.responsavel.toLowerCase().includes(search)
    );
  }, [disponiveis, searchLeft]);

  const filteredLinked = useMemo(() => {
    const search = searchRight.trim().toLowerCase();
    if (!search) return vinculados;
    return vinculados.filter(l =>
      l.local_nome.toLowerCase().includes(search) ||
      l.responsavel.toLowerCase().includes(search)
    );
  }, [vinculados, searchRight]);

  const toggleAvailableSelection = (id: number) => {
    setSelectedAvailable(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleLinkedSelection = (id: number) => {
    setSelectedLinked(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleAllAvailable = () => {
    if (selectedAvailable.length === filteredAvailable.length) {
      setSelectedAvailable([]);
    } else {
      setSelectedAvailable(filteredAvailable.map(l => l.id));
    }
  };

  const toggleAllLinked = () => {
    if (selectedLinked.length === filteredLinked.length) {
      setSelectedLinked([]);
    } else {
      setSelectedLinked(filteredLinked.map(l => l.local_contagem_id));
    }
  };

  const handleLinkSelected = async () => {
    if (selectedAvailable.length === 0) return;
    try {
      setLinking(true);
      await clienteHospitalLocalService.vincularLocais(clienteId, hospitalId, selectedAvailable);
      await loadData();
      setSelectedAvailable([]);
      msg.success(`${selectedAvailable.length} local(is) vinculado(s) com sucesso!`);
    } catch (error: any) {
      console.error('Erro ao vincular locais:', error);
      msg.error(error?.response?.data?.detail || 'Erro ao vincular locais');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkSelected = async () => {
    if (selectedLinked.length === 0) return;
    const confirmed = await confirm({
      title: 'Desvincular Locais',
      message: `Deseja realmente desvincular ${selectedLinked.length} local(is) deste cliente/hospital?`,
      variant: 'danger',
      confirmText: 'Desvincular',
    });
    if (!confirmed) return;

    try {
      setUnlinking(true);
      for (const localId of selectedLinked) {
        await clienteHospitalLocalService.desvincularLocal(clienteId, hospitalId, localId);
      }
      await loadData();
      setSelectedLinked([]);
      msg.success(`${selectedLinked.length} local(is) desvinculado(s) com sucesso!`);
    } catch (error: any) {
      console.error('Erro ao desvincular locais:', error);
      msg.error(error?.response?.data?.detail || 'Erro ao desvincular locais');
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <>
      <ConfirmDialog />
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <MapPin className="w-6 h-6 text-blue-600" />
              Vincular Locais de Contagem
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="px-6 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Hospital: <strong>{hospitalNome}</strong>
            </p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden p-6">
            {loadingData ? (
              <div className="py-12 flex flex-col items-center justify-center text-gray-500">
                <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
                Carregando locais...
              </div>
            ) : (
              <div className="flex gap-4 h-[450px]">
                {/* Left: Available */}
                <div className="flex-1 flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Locais Disponiveis</h4>
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">
                        {filteredAvailable.length}
                      </span>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchLeft}
                        onChange={(e) => setSearchLeft(e.target.value)}
                        placeholder="Buscar por nome, responsavel..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    {filteredAvailable.length > 0 && (
                      <label className="flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200">
                        <input
                          type="checkbox"
                          checked={selectedAvailable.length === filteredAvailable.length && filteredAvailable.length > 0}
                          onChange={toggleAllAvailable}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Selecionar todos
                      </label>
                    )}
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-1">
                    {filteredAvailable.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-sm text-gray-500 dark:text-gray-400">
                        {searchLeft ? 'Nenhum local encontrado' : 'Todos os locais ja estao vinculados'}
                      </div>
                    ) : (
                      filteredAvailable.map((local) => (
                        <label
                          key={local.id}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedAvailable.includes(local.id)
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedAvailable.includes(local.id)}
                            onChange={() => toggleAvailableSelection(local.id)}
                            className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 dark:text-gray-100">{local.local}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              Resp: {local.responsavel}
                              {local.telefone && ` | Tel: ${local.telefone}`}
                            </p>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* Center: Arrows */}
                <div className="flex flex-col items-center justify-center gap-3 w-28">
                  <button
                    onClick={handleLinkSelected}
                    disabled={selectedAvailable.length === 0 || linking}
                    className="w-full px-4 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg disabled:shadow-none flex items-center justify-center gap-2"
                    title="Vincular selecionados"
                    type="button"
                  >
                    {linking ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5 stroke-[2.5]" />}
                  </button>
                  <button
                    onClick={handleUnlinkSelected}
                    disabled={selectedLinked.length === 0 || unlinking}
                    className="w-full px-4 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg disabled:shadow-none flex items-center justify-center gap-2"
                    title="Desvincular selecionados"
                    type="button"
                  >
                    {unlinking ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowLeft className="w-5 h-5 stroke-[2.5]" />}
                  </button>
                </div>

                {/* Right: Linked */}
                <div className="flex-1 flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Locais Vinculados</h4>
                      <span className="text-xs font-medium text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded">
                        {filteredLinked.length}
                      </span>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchRight}
                        onChange={(e) => setSearchRight(e.target.value)}
                        placeholder="Buscar vinculados..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    {filteredLinked.length > 0 && (
                      <label className="flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200">
                        <input
                          type="checkbox"
                          checked={selectedLinked.length === filteredLinked.length && filteredLinked.length > 0}
                          onChange={toggleAllLinked}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Selecionar todos
                      </label>
                    )}
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-1">
                    {filteredLinked.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-sm text-gray-500 dark:text-gray-400">
                        {searchRight ? 'Nenhum local encontrado' : 'Nenhum local vinculado'}
                      </div>
                    ) : (
                      filteredLinked.map((local) => (
                        <label
                          key={local.local_contagem_id}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedLinked.includes(local.local_contagem_id)
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedLinked.includes(local.local_contagem_id)}
                            onChange={() => toggleLinkedSelection(local.local_contagem_id)}
                            className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm text-gray-900 dark:text-gray-100 flex-1">{local.local_nome}</p>
                              <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium">
                                OK
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              Resp: {local.responsavel}
                              {local.telefone && ` | Tel: ${local.telefone}`}
                            </p>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Status bar */}
            {!loadingData && (selectedAvailable.length > 0 || selectedLinked.length > 0) && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {selectedAvailable.length + selectedLinked.length} local(is) selecionado(s)
                  {selectedAvailable.length > 0 && ` · ${selectedAvailable.length} para vincular`}
                  {selectedLinked.length > 0 && ` · ${selectedLinked.length} para desvincular`}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <button
              type="button"
              onClick={() => { onSave(); onClose(); }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Save className="w-4 h-4" />
              Concluir
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default VincularLocaisModal;
